import { AnimatedNumber } from '../AnimatedNumber';
import { useRef, useState, type CSSProperties } from 'react';
import { CoinChart } from './CoinChart';
import { PredictionStatus } from './PredictionStatus';
import { ResultTag } from './ResultTag';
import { CoinStats, CrowdMeter } from './parts';
import { CoinIdentity } from './CoinIdentity';
import type { ArtRenderer, CoinView } from './model';
import type { Choice, OddsSelection } from '@pons/contracts';
import { useLiveOdds } from '../reflex/useLiveOdds';
import { cardVisualState } from './cardStateMotion';
import { BetBursts } from './BetBursts';
import { useBetBursts } from './useBetBursts';
import type { BetBurst } from './betBurstModel';
function step(index: number, className?: string) {
  return {
    className: className ? 'coin-step ' + className : 'coin-step',
    style: { '--step': String(index) } as CSSProperties,
  };
}
const callLabel = (choice: Choice) => (choice === 'SURVIVES' ? 'RUN!' : 'DIE!');
// Crowd-based multipliers are confined to simulated rounds.
const payout = (share: number) =>
  share > 0 ? (100 / share).toFixed(1) + 'x' : null;
function CallButton({
  choice,
  share,
  coefficient,
  disabled,
  onPredict,
  bursts,
}: {
  choice: Choice;
  share: number | null;
  coefficient?: string | null;
  disabled: boolean;
  onPredict: () => void;
  bursts: BetBurst[];
}) {
  const odds =
    coefficient === undefined
      ? share === null
        ? null
        : payout(share)
      : coefficient === null
        ? null
        : coefficient + 'x';
  return (
    <button
      type="button"
      className={choice === 'SURVIVES' ? 'is-up' : 'is-down'}
      // Inverse quoted coefficients set width; the minimum keeps labels readable.
      style={{ '--share': String(share ?? 50) } as CSSProperties}
      disabled={disabled}
      onClick={onPredict}
    >
      <span>{callLabel(choice)}</span>
      {odds && (
        <>
          <i aria-hidden="true">•</i>
          <span>
            <AnimatedNumber value={odds} />
          </span>
        </>
      )}
      <BetBursts bursts={bursts} />
    </button>
  );
}
export function CoinCard({
  coin,
  now,
  art,
  onPredict,
  titleId,
}: {
  coin: CoinView;
  now: number;
  art: ArtRenderer;
  onPredict?: (
    id: string,
    choice: Choice,
    odds?: OddsSelection,
  ) => void | boolean | Promise<boolean>;
  titleId?: string;
}) {
  const crowdOnly = coin.simulated && !coin.id.startsWith('game-');
  const choosing =
    !!onPredict &&
    !coin.choice &&
    now < coin.closesAt &&
    (coin.phase === 'open' || coin.phase === 'opening');
  const live = useLiveOdds(coin, choosing && !crowdOnly);
  const sending = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const quote = live.quote && live.quote.validUntil > now ? live.quote : null;
  const dieCoefficient = Number(quote?.coefficients.DIES);
  const runCoefficient = Number(quote?.coefficients.SURVIVES);
  const dieShare = quote
    ? (100 * runCoefficient) / (dieCoefficient + runCoefficient)
    : null;
  const disabled =
    !choosing ||
    coin.phase !== 'open' ||
    busy ||
    (coin.simulated && coin.stale) ||
    (!crowdOnly && !quote);
  async function predict(choice: Choice) {
    if (disabled || sending.current || !onPredict) return;
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      const selection =
        !crowdOnly && quote
          ? {
              amount: quote.amount,
              version: quote.version,
              coefficient: quote.coefficients[choice],
            }
          : undefined;
      const accepted = await onPredict(coin.id, choice, selection);
      if (accepted === false && !crowdOnly) live.reload();
    } catch {
      setError('Prediction could not be saved. Please try again.');
      if (!crowdOnly) live.reload();
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  const visualState = cardVisualState(coin, now);
  const bursts = useBetBursts(coin, now, choosing);
  let order = 0;
  const next = (className?: string) => step(order++, className);
  return (
    <article
      className={'coin-card is-' + coin.phase}
      data-card-state={visualState}
    >
      <header className="coin-head">
        <div {...next('coin-headings')}>
          <CoinIdentity coin={coin} titleId={titleId} />
        </div>
        <div {...next()}>{art(coin, 'coin-art')}</div>
      </header>
      <div {...next()}>
        <CoinChart coin={coin} />
      </div>
      <div {...next()}>
        <CoinStats
          coin={coin}
          status={
            <ResultTag
              coin={coin}
              now={now}
              layout="card"
              showNote={!onPredict}
            />
          }
        />
      </div>
      <hr className="coin-rule" />
      <div className="coin-call">
        <div {...next()}>
          <CrowdMeter
            coin={crowdOnly ? coin : { ...coin, odds: quote ?? undefined }}
          />
        </div>
        {(onPredict || coin.choice) && (
          <div {...next()}>
            <div className={'call-action' + (choosing ? ' is-choosing' : '')}>
              {onPredict && (
                <div
                  className={'call-split' + (!choosing ? ' is-hidden' : '')}
                  inert={!choosing}
                  aria-hidden={!choosing}
                >
                  <CallButton
                    choice="DIES"
                    bursts={bursts.filter((burst) => burst.side === 'DIES')}
                    share={
                      crowdOnly
                        ? coin.split === null
                          ? null
                          : 100 - coin.split
                        : dieShare
                    }
                    coefficient={
                      crowdOnly ? undefined : (quote?.coefficients.DIES ?? null)
                    }
                    disabled={disabled}
                    onPredict={() => void predict('DIES')}
                  />
                  <CallButton
                    choice="SURVIVES"
                    bursts={bursts.filter((burst) => burst.side === 'SURVIVES')}
                    share={
                      crowdOnly
                        ? coin.split
                        : dieShare === null
                          ? null
                          : 100 - dieShare
                    }
                    coefficient={
                      crowdOnly
                        ? undefined
                        : (quote?.coefficients.SURVIVES ?? null)
                    }
                    disabled={disabled}
                    onPredict={() => void predict('SURVIVES')}
                  />
                </div>
              )}
              {choosing || coin.choice ? (
                <PredictionStatus
                  coin={coin}
                  now={now}
                  amount={crowdOnly ? undefined : live.amount}
                  onAmount={crowdOnly ? undefined : live.setAmount}
                  disabled={busy}
                />
              ) : (
                <p className="call-line">
                  {now < coin.closesAt
                    ? 'Make your call in Arena'
                    : 'Prediction window closed'}
                </p>
              )}
            </div>
            {(error || (!crowdOnly && choosing && live.error)) && (
              <p className="call-line" role="status">
                {error || live.error}
              </p>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
